// Generic Input Component
// Should not be used directly, but should be consumed
// by other components, which also provide styling.

// ------------------------------------------------------------
// NOTE: Do not add ANY STYLES to this component
// This component has no storybook page
// ------------------------------------------------------------

/* eslint-disable react/prop-types */

import * as React from 'react';

import { useSlug } from '../hooks/useSlug';
import { GlobalProps, InputType } from '../types';
import { classes } from '../utils';

export type InputProps = GlobalProps & {
  autoComplete?: string;
  autoCorrect?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  id: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  label?: string;
  name?: string;
  onActiveStatusChange?: (active: boolean) => void;
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (ev: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  spellCheck?: boolean;
  type?: InputType;
  value?: string | number;
  defaultValue?: string | number;
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  function InputComponent(props, ref) {
    const {
      autoComplete,
      autoCorrect,
      checked,
      defaultChecked,
      className,
      defaultValue,
      disabled,
      error,
      id,
      inputProps = {},
      label,
      name = 'input',
      onChange,
      onActiveStatusChange,
      onKeyDown,
      placeholder,
      required = false,
      spellCheck,
      type,
      value,
    } = props;

    const [componentName] = useSlug(name);
    const mounted = React.useRef<boolean>(false);

    const [isFocused, setIsFocused] = React.useState(false);

    const isActive = isFocused || Boolean(value);

    const handleFocus = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (inputProps.autoFocus) {
          e.target.select();
        }

        setIsFocused(true);

        // call the upstream handler if one
        // was provided
        if (inputProps.onFocus) {
          inputProps.onFocus(e);
        }
      },
      [inputProps, isFocused]
    );

    // merge in any classes passed in +
    // active
    const cx = classes(
      [
        className,
        {
          active: isActive,
          error,
        },
      ],
      props
    );

    const currentValue = {
      ...(value !== 'undefined' && {
        value,
      }),
      ...(defaultValue !== 'undefined' && {
        defaultValue,
      }),
    };

    const handleBlur = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>): void => {
        // flag is not focused so we can hide label
        setIsFocused(false);

        // call any upstream listeners to blur
        if (inputProps.onBlur) {
          inputProps.onBlur(e);
        }
      },
      [inputProps.onBlur, isFocused]
    );

    React.useEffect(() => {
      // ensure this only fires after a real
      // change and not on initial render
      if (onActiveStatusChange && mounted.current) {
        onActiveStatusChange(isActive);
      }

      mounted.current = true;
    }, [isActive]);

    return (
      <input
        {...inputProps}
        {...currentValue}
        {...(checked !== undefined && {
          checked,
        })}
        {...(defaultChecked !== undefined && {
          defaultChecked,
        })}
        {...(autoComplete !== undefined && {
          autoComplete,
        })}
        {...(autoCorrect !== undefined && {
          autoCorrect,
        })}
        data-component={componentName}
        className={cx}
        disabled={inputProps.disabled || disabled}
        id={id}
        name={inputProps.name || name}
        onBlur={handleBlur}
        onChange={
          (inputProps.onChange as
            | React.ChangeEventHandler<HTMLInputElement>
            | undefined) || onChange
        }
        onKeyDown={
          (inputProps.onKeyDown as
            | React.KeyboardEventHandler<HTMLInputElement>
            | undefined) || onKeyDown
        }
        onFocus={handleFocus}
        placeholder={inputProps.placeholder || placeholder}
        ref={ref}
        required={required}
        spellCheck={spellCheck}
        type={(inputProps.type as InputType) || type}
        aria-invalid={Boolean(error)}
        aria-label={label || name}
      />
    );
  }
);
